import {DateTime} from "luxon";
import type {EBirdMyDataSchema} from "ebird-mydata-reader";
import {BASE_MONTHS} from "../../../../helpers/months";
import {VISIT_HISTORY_MIN_BAR_HEIGHT} from "./constants";
import type {VisitHistoryChartData, VisitHistoryMonth} from "./types";

function getObservationMonth(observation: EBirdMyDataSchema) {
    const observationDate = DateTime.fromISO(observation.date);
    return observationDate.isValid ? observationDate.month : undefined;
}

function getObservationVisitKey(observation: EBirdMyDataSchema) {
    return observation.submissionID ?? (observation as any).submissionId ?? `${observation.date}-${observation.time}-${observation.location}`;
}

function getPeakMonthsLabel(months: VisitHistoryMonth[]) {
    const peakMonths = months.filter(month => month.isPeak);
    if (!peakMonths.length) return '';

    return peakMonths.map(month => month.label).join(' - ');
}

export function getVisitHistoryChartData(observations: EBirdMyDataSchema[]): VisitHistoryChartData {
    const monthlyVisits = new Map<number, Set<string>>();

    for (let month = 1; month <= 12; month++) {
        monthlyVisits.set(month, new Set());
    }

    for (const observation of observations) {
        const month = getObservationMonth(observation);
        if (!month) continue;

        monthlyVisits.get(month)?.add(getObservationVisitKey(observation));
    }

    const monthlyCounts = Array.from(monthlyVisits.values()).map(visits => visits.size);
    const peakCount = Math.max(0, ...monthlyCounts);

    const months = BASE_MONTHS.map(({value, label}) => {
        const count = monthlyVisits.get(value)?.size ?? 0;
        const heightPercent = peakCount && count > 0
            ? Math.max(VISIT_HISTORY_MIN_BAR_HEIGHT, Math.round((count / peakCount) * 100))
            : 0;

        return {
            month: value,
            label,
            count,
            heightPercent,
            hasVisits: count > 0,
            isPeak: peakCount > 0 && count === peakCount
        };
    });

    return {
        months,
        peakMonthsLabel: getPeakMonthsLabel(months),
        peakCount
    };
}
